import { ClerkProvider, SignInButton, UserButton, useAuth } from "@clerk/react";
import { createContext, useCallback, useContext, type ReactNode } from "react";

/**
 * Auth is intentionally decoupled from Clerk's client state. Identity
 * and role always come from the server (`session.me`), because:
 *
 *  - Locally we run WITHOUT Clerk. A `pk_live` production key refuses to
 *    initialise on localhost (Clerk's domain check), so local dev uses
 *    the backend's localhost dev-bypass instead — no key in the browser.
 *  - In production the Pages build sets VITE_CLERK_PUBLISHABLE_KEY and
 *    Clerk mounts for real sign-in.
 *
 * Clerk, when present, is used only to (a) render the sign-in UI and
 * (b) mint a bearer token for API calls.
 */

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;
export const clerkEnabled = Boolean(clerkKey);

type TokenGetter = () => Promise<string | null>;
const TokenContext = createContext<TokenGetter>(async () => null);
export const useTokenGetter = (): TokenGetter => useContext(TokenContext);

// Once we learn Clerk can't mint tokens on this host, stop waiting on it.
let clerkTokenUnavailable = false;

function ClerkTokenBridge({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  // Never let a Clerk failure block API calls. On a host where Clerk
  // refuses to initialise (e.g. a pk_live key on a non-vyaustralia.org
  // domain), getToken() can HANG forever — which would stall every
  // request, including public reads. Race it against a short timeout and
  // remember the outcome, so glossary / graph / dohas always load and
  // only the very first call pays the wait. Sign-in features stay
  // unavailable until the app is served from an allowed domain.
  const value = useCallback(async (): Promise<string | null> => {
    if (clerkTokenUnavailable) return null;
    try {
      const token = await Promise.race([
        getToken(),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error("clerk-token-timeout")), 2500),
        ),
      ]);
      return token;
    } catch {
      clerkTokenUnavailable = true;
      return null;
    }
  }, [getToken]);
  return (
    <TokenContext.Provider value={value}>{children}</TokenContext.Provider>
  );
}

const prefersDark =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

// Match the sign-in modal to the system theme.
const clerkAppearance = {
  variables: {
    colorPrimary: prefersDark ? "#d97757" : "#c15f3c",
    colorBackground: prefersDark ? "#221e18" : "#fbf8f1",
    colorText: prefersDark ? "#ece5d6" : "#1f1d1a",
    colorInputBackground: prefersDark ? "#1a1712" : "#ffffff",
    colorInputText: prefersDark ? "#ece5d6" : "#1f1d1a",
    borderRadius: "8px",
    fontFamily: '"Alegreya Sans", sans-serif',
  },
} as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!clerkKey) return <>{children}</>;
  return (
    <ClerkProvider
      publishableKey={clerkKey}
      afterSignOutUrl="/"
      appearance={clerkAppearance}
    >
      <ClerkTokenBridge>{children}</ClerkTokenBridge>
    </ClerkProvider>
  );
}

/** Signed-in state comes from the `useAuth` hook — this version of
 *  @clerk/react doesn't ship the <SignedIn>/<SignedOut> control
 *  components. Only mounted inside ClerkProvider (see AuthControls). */
function ClerkAuthControls() {
  const { isSignedIn } = useAuth();
  if (isSignedIn) return <UserButton />;
  return (
    <SignInButton mode="modal">
      <button
        type="button"
        className="rounded-full border border-line-strong px-4 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk"
      >
        Sign in
      </button>
    </SignInButton>
  );
}

/**
 * Sign-in / account controls for the nav. Renders nothing in local
 * public mode (identity is supplied by the dev-bypass server-side).
 */
export function AuthControls() {
  if (!clerkEnabled) return null;
  return <ClerkAuthControls />;
}
